import { RadioGroup } from '@chakra-ui/react';
import { ReactNode } from 'react';

export interface SegmentedControlOption<T extends string> {
  label: ReactNode;
  value: T;
}

interface SegmentedControlProps<T extends string> {
  name: string;
  /** Accessible name for the group, e.g. "Contracts view" */
  label: string;
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  isDisabled?: boolean;
}

/**
 * Compact single-choice toggle for switching views (e.g. Cards | Table).
 * Built on native radios, so arrow keys move the selection. Sized to sit
 * beside 32px inputs and icon buttons in page headers.
 *
 * v3 port: composes `RadioGroup.Root`/`Item` the way `RadioStyleButtonGroup`
 * does, since v2's `useRadio`/`useRadioGroup` hooks are gone. The item is
 * the whole segment, so no `ItemControl`/`ItemText` (their recipe styles
 * would fight the segment's own color and type).
 */
export const SegmentedControl = <T extends string>({
  name,
  label,
  options,
  value,
  onChange,
  isDisabled,
}: SegmentedControlProps<T>) => (
  <RadioGroup.Root
    name={name}
    value={value}
    disabled={isDisabled}
    onValueChange={({ value: nextValue }) => {
      if (nextValue != null) {
        onChange(nextValue as T);
      }
    }}
    aria-label={label}
    display="inline-flex"
    flexDirection="row"
    gap="2px"
    p="2px"
    h="32px"
    borderWidth="1px"
    borderStyle="solid"
    borderColor="border.default"
    borderRadius="md"
    bg="bg.surface"
  >
    {options.map(({ label: optionLabel, value: optionValue }) => (
      <RadioGroup.Item
        key={optionValue}
        value={optionValue}
        display="flex"
        alignItems="center"
        px={2.5}
        borderRadius="sm"
        fontSize="xs"
        fontWeight={500}
        color="fg.muted"
        whiteSpace="nowrap"
        cursor="pointer"
        transition="background-color 0.12s ease, color 0.12s ease"
        _hover={{ bg: 'bg.hover', color: 'fg.primary' }}
        _checked={{
          bg: 'bg.selected',
          color: 'action.primaryActive',
          fontWeight: 600,
          _hover: { bg: 'bg.selected' },
        }}
        _focusVisible={{ boxShadow: 'focus' }}
        _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
      >
        <RadioGroup.ItemHiddenInput />
        {optionLabel}
      </RadioGroup.Item>
    ))}
  </RadioGroup.Root>
);
